'use client'

import type {} from '@mui/lab/themeAugmentation'
import { inputBaseClasses, inputLabelClasses, ratingClasses, svgIconClasses } from '@mui/material'
import { Theme, createTheme } from '@mui/material/styles'
import type {} from '@mui/x-date-pickers/themeAugmentation'
import avatarOverrides from '../Avatar/theme'
import buttonOverrides from '../Button/theme'
import datePickerOverrides from '../DatePicker/theme'
import dialogOverrides from '../Dialog/theme'
import flagGlobalCss from '../Flag/globalCss'
import typographyOverrides from '../Typography/theme'
import { hasLimitedMemory } from '../device'
import { palette } from '../palette'
import { colors, typography } from '../tokens'
import appLayoutStyles from './appLayout'
import { breakpoints } from './breakpoints'
import fontStyles from './fonts'
import layoutStyles from './layout'
import leaderboardStyles from './leaderboard'
import snackbarStyles from './snackbar'
import { InterNationsThemeOptions } from './types'
import utilClasses from './utilClasses'

// Not exported, since there's a similar function defined on the theme: theme.typography.pxToRem().
const pxToRem = (px: number): string => `${px / 16}rem`

const shape = { borderRadius: 6 }

type Breakpoint = keyof typeof breakpoints

export { breakpoints }
export type { Breakpoint }

const disableAnimation = `
*,
*::before,
*::after {
    transition: none !important;
    animation: none !important;
}
`

const globalCss = (theme: Theme, assetsBaseUrl: string) => `
    ${fontStyles(assetsBaseUrl)}

    /*
     * setting height to html, body, and #__next to use with the full height needed in messaging since
     * 100vh is not 100%, see, https://dev.to/admitkard/mobile-issue-with-100vh-height-100-100vh-3-solutions-3nae (solution 1)
     */

    html {
        text-size-adjust: none;
        height: 100%;
    }

    body {
        font-size: ${pxToRem(14)};
        line-height: ${20 / 14};
        letter-spacing: ${pxToRem(0.2)};
        height: 100%;
        margin: 0;
        background-color: ${theme.palette.background.paper};
    }

    #__next {
        height: 100%;
    }

    a {
        color: ${colors.blue};
        text-decoration: none;
    }

    strong {
        font-weight: 600;
    }

    fieldset {
        border: none;
        padding: 0;
        margin: 0;
    }

    ${layoutStyles}

    ${appLayoutStyles}

    ${leaderboardStyles}

    ${utilClasses}

    ${snackbarStyles}

    ${
        hasLimitedMemory()
            ? disableAnimation
            : `
            @media (prefers-reduced-motion) {
                ${disableAnimation}
            }`
    }
`

export default ({ assetsBaseUrl = '/' }: InterNationsThemeOptions): Theme => {
    let theme = createTheme({
        assetsBaseUrl,
        shape,
        palette,
        typography: {
            fontFamily: typography.fontFamilyDefault,
            // Use relative units for font metrics. Although zooming would work with px values as well, relative units
            // ensure that browser font settings are taken into account.
            //
            // For details, see https://www.24a11y.com/2019/pixels-vs-relative-units-in-css-why-its-still-a-big-deal/
            h1: {
                fontFamily: typography.fontFamilyBrand,
                fontWeight: 400,
                fontSize: pxToRem(36),
                lineHeight: 1.15,
                letterSpacing: pxToRem(-1.38),
            },
            h2: {
                fontFamily: typography.fontFamilyBrand,
                fontWeight: 400,
                fontSize: pxToRem(26),
                lineHeight: 1.15,
                letterSpacing: pxToRem(-0.2),
            },
            h3: {
                fontFamily: typography.fontFamilyBrand,
                fontWeight: 600,
                fontSize: pxToRem(22),
                lineHeight: 1.15,
                letterSpacing: 'normal',
            },
            h4: {
                fontFamily: typography.fontFamilyBrand,
                fontWeight: 600,
                fontSize: pxToRem(18),
                lineHeight: 1.2,
                letterSpacing: pxToRem(-0.2),
                textTransform: 'none',
                '& > a:hover, a&:hover': {
                    color: palette.primary.dark,
                    textDecoration: 'none',
                },
            },
            subtitle1: {
                fontWeight: 600,
                fontSize: pxToRem(16),
                lineHeight: 1.5,
                letterSpacing: 'normal',
                textTransform: 'none',
            },
            subtitle2: {
                fontWeight: 600,
                fontSize: pxToRem(14),
                lineHeight: 1.3,
                letterSpacing: pxToRem(0.2),
                textTransform: 'none',
            },
            body1: {
                fontSize: pxToRem(16),
                lineHeight: 1.5,
                letterSpacing: 'normal',
            },
            body2: {
                fontSize: pxToRem(14),
                lineHeight: 20 / 14,
                letterSpacing: pxToRem(0.2),
            },
            button: {
                fontFamily: typography.fontFamilyBrand,
                fontWeight: 700,
                fontSize: pxToRem(14),
                lineHeight: 1, // Not supposed to wrap
                letterSpacing: 'normal',
            },
            caption: {
                fontSize: pxToRem(12),
                lineHeight: 1.3,
                letterSpacing: pxToRem(0.4),
                textTransform: 'none',
            },
            overline: {
                fontWeight: 600,
                fontSize: pxToRem(12),
                lineHeight: 1, // Not supposed to wrap
                letterSpacing: pxToRem(0.5),
            },
        },
        breakpoints: {
            values: breakpoints,
        },
        components: {
            MuiButtonBase: {
                styleOverrides: {
                    root: {
                        height: 40,
                    },
                },
                defaultProps: {
                    disableRipple: hasLimitedMemory(),
                },
            },
            MuiRating: {
                styleOverrides: {
                    root: {
                        [`&.${ratingClasses.readOnly}`]: {
                            color: palette.primary.main,
                        },
                        [`&.${ratingClasses.readOnly} .${ratingClasses.iconEmpty}`]: {
                            color: palette.primary.main,
                        },
                    },
                    sizeLarge: {
                        [`& .${svgIconClasses.root}`]: { fontSize: '32px' },
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        background: palette.common.white,
                        lineHeight: pxToRem(19),
                        [`&.${inputBaseClasses.sizeSmall}`]: {
                            // Adjust padding for 40px total height
                            paddingTop: 10,
                            paddingBottom: 10,
                        },
                    },
                    input: {
                        height: pxToRem(19),
                        // Adjust padding for 48px total height
                        paddingTop: 14.5,
                        paddingBottom: 14.5,
                    },
                    inputMultiline: {
                        padding: 0,
                    },
                    notchedOutline: {
                        borderColor: palette.grey[500],
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        lineHeight: pxToRem(19),
                    },
                    outlined: {
                        // override the whole transform rule to adjust translateY
                        transform: 'translate(14px, 14px) scale(1)',
                        '&.Mui-focused': {
                            transform: 'translate(14px, -7px) scale(.75)',
                        },
                        [`&.${inputLabelClasses.shrink}`]: {
                            transform: 'translate(14px, -7px) scale(.75)',
                        },
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    inputRoot: {
                        // Fighting specificity: .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]
                        paddingTop: '6px !important',
                        paddingBottom: '4px !important',
                    },
                    input: {
                        padding: '9.5px 4px !important',
                        minWidth: 0,
                    },
                    clearIndicator: {
                        height: 'auto',
                    },
                    popupIndicator: {
                        height: 'auto',
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        height: 28,
                        backgroundColor: palette.grey[300],
                    },
                    deleteIcon: {
                        marginRight: 2,
                    },
                    avatar: {
                        // Fighting specificity: .MuiChip-root .MuiChip-avatar
                        marginLeft: '3px !important',
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        '&$expanded': {
                            margin: 'none',
                        },
                    },
                },
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        background: 'none !important',
                    },
                },
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    labelPlacementStart: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginLeft: 0,
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        marginRight: 0,
                    },
                },
            },
            MuiBadge: {
                styleOverrides: {
                    badge: {
                        height: 16,
                        minWidth: 16,
                        padding: '0 3px',
                        fontWeight: 600,
                    },
                    anchorOriginTopRightRectangular: {
                        top: 4,
                    },
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        fontSize: '1.25rem',
                    },
                    fontSizeSmall: {
                        fontSize: '1rem',
                    },
                },
            },
            MuiListItemIcon: {
                styleOverrides: {
                    root: {
                        minWidth: 32,
                    },
                },
            },
            MuiListItem: {
                styleOverrides: {
                    divider: {
                        '&:last-child': {
                            borderBottom: 'none',
                        },
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        minHeight: 48,
                        paddingLeft: 8,
                        paddingRight: 8,
                    },
                    divider: {
                        '&:last-child': {
                            borderBottom: 'none',
                        },
                    },
                },
            },
            MuiListItemText: {
                styleOverrides: {
                    multiline: {
                        marginTop: 4,
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        background: palette.common.white,
                    },
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: {
                        // Fighting against MUI, which insists on reducing the height to 48px on MOB
                        minHeight: '56px !important',
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        // Legacy: copied over from classic website
                        boxShadow: '0 1px 5px rgba(51, 51, 51, 0.25)',
                        border: 'none',
                    },
                    colorDefault: {
                        backgroundColor: palette.common.white,
                    },
                },
                defaultProps: {
                    elevation: 0,
                    color: 'default',
                },
            },
            MuiTimeline: {
                styleOverrides: {
                    root: {
                        paddingLeft: 0,
                        marginTop: 0,
                    },
                },
            },
            MuiTimelineOppositeContent: {
                styleOverrides: {
                    root: {
                        flex: 0,
                        padding: 0,
                    },
                },
            },
            MuiTimelineConnector: {
                styleOverrides: {
                    root: {
                        backgroundColor: palette.divider,
                    },
                },
            },
            MuiTimelineContent: {
                styleOverrides: {
                    root: {
                        paddingTop: 4,
                    },
                },
            },
            MuiTimelineItem: {
                styleOverrides: {
                    root: {
                        '&:last-child': {
                            minHeight: 0,
                        },
                    },
                },
            },
            MuiSkeleton: {
                styleOverrides: {
                    text: {
                        borderRadius: shape.borderRadius,
                    },
                },
            },
            MuiPaper: {
                defaultProps: {
                    variant: 'outlined',
                },
                styleOverrides: {
                    rounded: {
                        borderRadius: 2 * shape.borderRadius,
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    select: {
                        minWidth: 16,
                        minHeight: pxToRem(19),
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: {
                        alignItems: 'center',
                    },
                    scrollableX: {
                        margin: '0 auto',
                        flexGrow: 0,
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    textColorInherit: {
                        '&.Mui-selected': {
                            color: colors.blue,
                        },
                    },
                },
            },
            MuiTabPanel: {
                styleOverrides: {
                    root: {
                        padding: 0,
                        paddingTop: 24,
                    },
                },
            },
            MuiMenu: {
                defaultProps: {
                    PaperProps: {
                        variant: 'elevation',
                        sx: {
                            borderRadius: 1,
                        },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    // Used by the date picker
                    sizeSmall: {
                        height: 'auto',
                    },
                },
            },
        },
    })

    // Additional classes and styles that rely on the `theme` values defined above
    theme = createTheme(theme, {
        components: {
            ...dialogOverrides(theme),
            ...avatarOverrides(theme),
            ...buttonOverrides(theme),
            ...typographyOverrides(theme),
            ...datePickerOverrides(),

            // Lastly, write the global CSS
            MuiCssBaseline: {
                styleOverrides: `
                    ${globalCss(theme, assetsBaseUrl)}
                    ${flagGlobalCss(theme)}
                `,
            },
        },
        transitions: hasLimitedMemory()
            ? {
                  // Apply `transition: none;` everywhere
                  create: () => 'none',
              }
            : {},
    })

    return theme
}
